import { useMutation } from "@apollo/client/react";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { object, string } from "yup";
import { Form, Formik } from "formik";
import type { ErrorLike } from "@apollo/client/core";
import { gql } from "graphql-tag";
import { ClientFacingErrors } from "@portal/app/_common/types/clientErrors";
import { useState, useEffect, useMemo, useRef } from "react";
import { LoginWithCodeDocument } from "./generated";
import { captureException } from "@sentry/nextjs";
import { detectKnownClientError } from "@portal/app/_common/lib/clientErrorUtils";
import { ValidatedFormikField } from "@portal/app/_components/ui-elements/formHelpers";
import { dreamThrottle } from "@portal/app/_common/lib/dreamThrottle";
import { LOCAL_STORAGE_KEYS, useLocalStorage } from "@portal/app/_components/hooks/useLocalStorage";
import { logger } from "@portal/app/_common/lib/logger";
import {
  CodeInput,
  verificationCodeLength,
} from "@portal/app/_components/flow/steps/PhoneVerificationStep";
import {
  NO_SHARED_LOGINS_ERROR,
  SIGN_IN_METHOD,
  useSignInContext,
} from "@portal/app/_components/flow/steps/SignInStep";
import { useFlowContext } from "@portal/app/_components/flow/FlowContext";
import { ButtonSkeleton } from "@portal/app/_components/ui-elements/Mui/DreamMuiSkeleton";
import Skeleton from "@mui/material/Skeleton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMessages } from "@fortawesome/pro-solid-svg-icons/faMessages";
import { useSignUpAnalytics } from "@portal/app/_components/hooks/useSignUpAnalytics";
import { useApolloCache } from "@portal/app/_components/apolloUtils";
import { RequestCodeViaEmailDocument, RequestCodeViaTextDocument } from "./generated";

void gql`
  mutation loginWithCode(
    $code: String!
    $email: Email
    $phone: String
    $affiliateSlug: String
    $affiliateExternalId: String
    $affiliateFirstEncounteredAt: String
  ) {
    loginWithCode(
      code: $code
      email: $email
      phone: $phone
      affiliateSlug: $affiliateSlug
      affiliateExternalId: $affiliateExternalId
      affiliateFirstEncounteredAt: $affiliateFirstEncounteredAt
    ) {
      id
      phoneVerified
      emailVerified
      tokens {
        accessToken
        refreshToken
      }
      customer {
        id
        signInFlow {
          ...SignInFlowFragment
        }
      }
    }
  }
`;

type FormValues = { code: string };
export const Code = () => {
  const { setClientErrorMessage } = useSignInContext();
  const { getFlowArgs } = useFlowContext();
  const [loginWithCode, { loading }] = useMutation(LoginWithCodeDocument);
  const [requestCodeViaText, { loading: textCodeRequestLoading }] = useMutation(
    RequestCodeViaTextDocument,
    {
      onCompleted: () => {
        setClientErrorMessage(null);
        setCanResendCode(false);
      },
    }
  );
  const [requestCodeViaEmail, { loading: emailCodeRequestLoading }] = useMutation(
    RequestCodeViaEmailDocument,
    {
      onCompleted: () => {
        setClientErrorMessage(null);
        setCanResendCode(false);
      },
    }
  );
  const { evictRootQuery } = useApolloCache();
  const initialized = useRef(false);
  const [method] = useLocalStorage<string>(LOCAL_STORAGE_KEYS.MAGIC_LINK_METHOD);
  const [code] = useLocalStorage<string>(LOCAL_STORAGE_KEYS.MAGIC_LINK_CODE);
  const [email] = useLocalStorage<string>(LOCAL_STORAGE_KEYS.MAGIC_LINK_EMAIL);
  const [phone] = useLocalStorage<string>(LOCAL_STORAGE_KEYS.MAGIC_LINK_PHONE);
  const [canResendCode, setCanResendCode] = useState(false);
  const getSignUpAnalytics = useSignUpAnalytics();

  const onSubmit = useMemo(
    () =>
      dreamThrottle((code: string) => {
        setClientErrorMessage(null);
        return loginWithCode({
          variables: {
            code,
            ...(method === SIGN_IN_METHOD.EMAIL ? { email } : { phone }),
            ...getSignUpAnalytics(),
            ...getFlowArgs(),
          },
          fetchPolicy: "network-only",
        })
          .then(() => {
            /**
             * When you're an admin, there's no customer ID. Without a customer ID,
             * the signInFlow field on customer will not return. This forces a
             * recalculation of the signInFlow steps.
             */
            evictRootQuery("signInFlow");
          })
          .catch((error: ErrorLike) => {
            const clientError = detectKnownClientError(error);
            if (clientError === ClientFacingErrors.RateLimit) {
              setClientErrorMessage({
                title: "You tried to log in too many times",
                description: "You will need to wait a little before trying another code.",
              });
            } else if (clientError === ClientFacingErrors.InvalidVerifyCode) {
              setClientErrorMessage({
                title: "Invalid code",
                description: "This code is either incorrect or expired. Please try again.",
              });
              setCanResendCode(true);
            } else if (clientError === ClientFacingErrors.UserIsSharedGoogleAccount) {
              setClientErrorMessage(NO_SHARED_LOGINS_ERROR);
            } else {
              captureException(error);
              logger.error(error);
              setClientErrorMessage({
                title: "That code didn't work",
                description:
                  "The code you used is wrong or expired. Please re-enter the code or start over to get a new code.",
              });
            }
          });
      }),
    [
      email,
      evictRootQuery,
      getFlowArgs,
      getSignUpAnalytics,
      loginWithCode,
      method,
      phone,
      setClientErrorMessage,
      setCanResendCode,
    ]
  );

  useEffect(() => {
    if (!initialized.current) {
      if (code) {
        void onSubmit(code);
      }
      initialized.current = true;
    }
  }, [code, onSubmit]);

  return (
    <Formik<FormValues>
      initialValues={{ code: "" }}
      onSubmit={async ({ code }, { resetForm }) => {
        resetForm();
        await onSubmit(code);
      }}
      validateOnMount={false}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={object().shape({
        code: string()
          .required(
            method === SIGN_IN_METHOD.TEXT
              ? "Enter the code we texted you."
              : method === SIGN_IN_METHOD.EMAIL
                ? "Enter the code we emailed you."
                : "Enter the code we sent you."
          )
          .matches(
            new RegExp(`^[0-9]{${verificationCodeLength}}$`, "g"),
            `The code is ${verificationCodeLength} numbers long.`
          ),
      })}
    >
      {({ isSubmitting, submitForm, values }) => {
        if (!isSubmitting && values.code.length === verificationCodeLength) {
          void submitForm();
        }
        return (
          <Form noValidate data-testid="code-form">
            <Typography variant="new_h2" sx={{ mb: 1, mt: 1 }}>
              <FontAwesomeIcon icon={faMessages} /> Enter {verificationCodeLength}-digit number sent
              to
            </Typography>
            <Typography variant="new_h2" sx={{ mb: 1, mt: 0 }}>
              {phone && method === SIGN_IN_METHOD.TEXT
                ? `+1 ••• ••• ${phone.substring(6)}`
                : email && method === SIGN_IN_METHOD.EMAIL
                  ? `${email.substr(0, 4)}...${email.substr(email.indexOf("@"))}`
                  : ""}
            </Typography>
            {loading ? (
              <>
                <Skeleton variant="rounded" sx={{ height: "4.25rem", width: "100%" }} />
                <ButtonSkeleton sx={{ mt: 1 }} />
              </>
            ) : (
              <>
                <ValidatedFormikField
                  as={CodeInput}
                  name="code"
                  data-testid="code"
                  disabled={loading}
                  autoFocus
                  inputProps={{
                    placeholder: new Array(verificationCodeLength).fill("_").join(" "),
                    inputMode: "numeric",
                    autoComplete: "one-time-code",
                    maxLength: verificationCodeLength,
                    "data-hj-allow": true,
                  }}
                />
                <Button
                  type="submit"
                  data-testid="submit"
                  fullWidth
                  sx={{ mt: 1 }}
                  disabled={loading}
                >
                  Submit code
                </Button>
                {canResendCode &&
                  ((phone && method === SIGN_IN_METHOD.TEXT) ||
                    (email && method === SIGN_IN_METHOD.EMAIL)) && (
                    <Button
                      variant="text"
                      sx={{ mt: 1 }}
                      disabled={textCodeRequestLoading || emailCodeRequestLoading}
                      onClick={() =>
                        method === SIGN_IN_METHOD.TEXT
                          ? requestCodeViaText({ variables: { phone: phone! } })
                          : requestCodeViaEmail({ variables: { email: email! } })
                      }
                    >
                      Resend code
                    </Button>
                  )}
              </>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};
